/**
 * People: the list, the two ways in, the parse review, and one person’s profile.
 *
 * **Order is load-bearing here.** `candidate-upload` and `candidate-review` must precede
 * `candidate-detail` (`/candidates/$candidateId`), or the router reads `upload` as an id and
 * serves the profile screen for a candidate that does not exist.
 */

import type { RouteManifestEntry } from '../route-manifest.js';

export const CANDIDATE_ROUTES: readonly RouteManifestEntry[] = [
  // Candidates carry their own entitlement, unlike jobs: `candidates` is a module an agency buys
  // (it is in every tenant's seeded set), and the person graph is a distinct capability from
  // client management rather than a part of it.
  {
    id: 'candidates',
    path: '/candidates',
    entitlement: 'candidates',
    authenticated: true,
    /*
      Third, ahead of the Funnel — 30 Aug 2026, asked for directly.
      The section reads as the order of the work: a client, a requisition, the people you might put
      on it, then the desk that tracks them. The Funnel is a view over the two above it, so it
      belongs after them rather than between the requisition and the people it is made of.
    */
    nav: { labelKey: 'shell.nav.candidates', section: 'recruiting', order: 3 },
    load: () => import('../screens/candidates.js'),
  },
  {
    /*
     * Beside the candidate list rather than inside Settings, because a pool is working material —
     * a shortlist you build while sourcing — not a configuration an admin sets once. Same
     * entitlement as the list it draws from; no extra permission, since `talent-pools.ts` treats
     * building a list as `read candidate`.
     */
    id: 'talent-pools',
    path: '/candidates/pools',
    entitlement: 'candidates',
    authenticated: true,
    load: () => import('../screens/talent-pools.js'),
  },
  {
    /*
     * One pool and who is on it — the screen a pool card opens.
     *
     * **After `/candidates/pools` in this list and before `/candidates/$candidateId`**, which is
     * the ordering rule this file's header states: the router reads the manifest in order, so a
     * two-segment path has to be declared before the one-segment parametric route that would
     * otherwise swallow its first segment.
     */
    id: 'talent-pool',
    path: '/candidates/pools/$poolId',
    entitlement: 'candidates',
    authenticated: true,
    load: () => import('../screens/talent-pool.js'),
  },
  {
    id: 'candidate-new',
    path: '/candidates/new',
    entitlement: 'candidates',
    // The widest write in the product. A sourcer may add a person where they may not open a
    // requisition — putting people in the database is what a sourcer is for.
    requires: { action: 'write', resource: 'candidate' },
    authenticated: true,
    load: () => import('../screens/candidate-new.js'),
  },
  // Upload and review sit *before* `/candidates/$candidateId` in this list, and the order is not
  // cosmetic: TanStack Router would otherwise be free to read `/candidates/upload` as a candidate
  // whose id is the word "upload", and the screen would 404 against an id that is not a uuid.
  {
    id: 'candidate-upload',
    path: '/candidates/upload',
    /*
     * The requisition these CVs are being read for — `jobId`, added 04 Oct 2026.
     *
     * Asked for as *"this Add a candidate should allow the same bulk upload, and accepting it
     * should come directly sourced"*. A recruiter working a job wants to drop ten CVs on it, not
     * to upload ten people and then add each of them to the job from somewhere else.
     *
     * A search param rather than a second route, for the reason the duplicate queue above gives:
     * it is the same screen doing the same work, carrying one more fact. It also makes the
     * job-scoped upload a link somebody can send.
     *
     * Optional, and the screens work exactly as before without it — the upload surface is reached
     * from the candidates area far more often than from a requisition.
     */
    validateSearch: (search) =>
      typeof search['jobId'] === 'string' ? { jobId: search['jobId'] } : {},
    entitlement: 'candidates',
    // The same write a manual add needs. Bulk upload is not a wider capability than typing a
    // person in — it is the same act, two hundred times.
    requires: { action: 'write', resource: 'candidate' },
    authenticated: true,
    load: () => import('../screens/candidate-upload.js'),
  },
  {
    id: 'candidate-review',
    path: '/candidates/review/$resumeId',
    // Carried from the upload screen, and onward to the next document in the queue — see the note
    // above. The accept uses it to put the person straight into that requisition's pipeline.
    validateSearch: (search) =>
      typeof search['jobId'] === 'string' ? { jobId: search['jobId'] } : {},
    entitlement: 'candidates',
    // Reviewing a parse is what *creates* the candidate, so it is a write. Gating it on `read`
    // would let a viewer reach a screen whose only button they cannot press.
    requires: { action: 'write', resource: 'candidate' },
    authenticated: true,
    load: () => import('../screens/candidate-review.js'),
  },
  {
    /*
     * Possible duplicates — C6. Beside the candidate list for the same reason talent pools are:
     * it is working material a recruiter clears, not a setting an admin configures.
     *
     * Before `/candidates/$candidateId` in this list, like upload and review, or the router is free
     * to read `duplicates` as a candidate id.
     */
    id: 'merge-review',
    path: '/candidates/duplicates',
    /*
     * One person's pairs, or the whole queue — `candidateId`, added 30 Aug 2026.
     *
     * The candidate record's *"check for duplicate records"* used to open this screen showing every
     * pair in the agency, which answers a question nobody asked from a profile. Narrowing it is a
     * search param rather than a second route because it is the same screen doing the same work on
     * a smaller set — and because a narrowed queue is then a link somebody can send.
     */
    validateSearch: (search) =>
      typeof search['candidateId'] === 'string' ? { candidateId: search['candidateId'] } : {},
    entitlement: 'candidates',
    // A write: both answers change the database, and one of them merges two people. Gating it on
    // `read` would put a viewer on a screen whose only two buttons they cannot press.
    requires: { action: 'write', resource: 'candidate' },
    authenticated: true,
    load: () => import('../screens/merge-review.js'),
  },
  /*
   * ── One record, five addresses — M-36, 30 Aug 2026 ────────────────────────────────────────────
   *
   * The profile became a layout with five tabs, and each tab is its own route rather than a piece
   * of component state. Three things depend on that and none is cosmetic:
   *
   *   1. **A link a recruiter can send.** "Look at her CV" is a URL, not a scroll position and not
   *      a tab somebody has to find after arriving.
   *   2. **The back button.** Tabs held in state make the browser's back button leave the record
   *      entirely, which is the usual way tabbed records feel broken.
   *   3. **The browser suite can reach them.** `every-screen.spec.ts` has never opened a candidate
   *      detail page because the route needs an id — which is exactly why a white screen reached
   *      the product owner on 30 Aug rather than a red spec. Five addresses are five things a spec
   *      can visit.
   *
   * The four sub-routes are declared **before** the parametric parent for the same reason upload
   * and review are declared before it: the router reads this list in order, and a two-segment path
   * has to be matched before the one-segment route that would otherwise swallow its first segment.
   *
   * They carry no `requires`. Reading a person's record is a read, and the write controls inside it
   * are gated individually — gating the route on `write` would put a viewer who may legitimately
   * read the record on a permission error instead.
   */
  {
    id: 'candidate-resumes',
    path: '/candidates/$candidateId/resumes',
    entitlement: 'candidates',
    authenticated: true,
    load: () => import('../screens/candidate/resumes.js'),
  },
  {
    id: 'candidate-applications',
    path: '/candidates/$candidateId/applications',
    entitlement: 'candidates',
    authenticated: true,
    load: () => import('../screens/candidate/applications.js'),
  },
  {
    id: 'candidate-communication',
    path: '/candidates/$candidateId/communication',
    entitlement: 'candidates',
    authenticated: true,
    load: () => import('../screens/candidate/communication.js'),
  },
  {
    id: 'candidate-activity',
    path: '/candidates/$candidateId/activity',
    entitlement: 'candidates',
    authenticated: true,
    load: () => import('../screens/candidate/activity.js'),
  },
  {
    id: 'candidate-detail',
    path: '/candidates/$candidateId',
    entitlement: 'candidates',
    authenticated: true,
    load: () => import('../screens/candidate/overview.js'),
  },
];
